import { Sun, Moon, Monitor, Fingerprint, Bell, LogOut, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Section } from '@shared/ui/containers/Section';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { ListItem } from '@shared/ui/lists/ListItem';
import { SegmentedControl } from '@shared/ui/lists/SegmentedControl';
import { ActionButton } from '@shared/ui/forms/FormFields';
import { useSettingsStore, type ThemePref } from '@shared/stores/settingsStore';
import { useAuthStore } from '@shared/stores/authStore';
import { env } from '@config/env';
import { pageCopy } from '@shared/lib/pageCopy';
import { toast } from '@shared/ui/feedback/Toast';
import { platform } from '@shared/lib/platform';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Checkbox } from '@shared/ui/primitives/Checkbox';
import { resetToHubContext } from '@shared/api/domainApi';
import { getEffectiveRootTenantId } from '@shared/lib/ssoRootScope';
import { showNativeSplash, hideNativeSplash, preloadBrandImages, delay, SPLASH_MAX_WAIT_MS } from '@shared/lib/appSplash';

/**
 * Settings — display, security, notifications, about, sign-out.
 * Native-only toggles (biometric unlock, push opt-in) hide on web.
 */
export function Settings(): JSX.Element {
  const settingsCopy = pageCopy().settings;
  const navigate = useNavigate();
  const theme = useSettingsStore((s) => s.theme);
  const setTheme = useSettingsStore((s) => s.setTheme);
  const pushEnabled = useSettingsStore((s) => s.pushEnabled);
  const setPushEnabled = useSettingsStore((s) => s.setPushEnabled);
  const biometricEnabled = useSettingsStore((s) => s.biometricEnabled);
  const setBiometricEnabled = useSettingsStore((s) => s.setBiometricEnabled);

  const onSignOut = async () => {
    if (!confirm('Sign out of this device?')) return;
    // SSO builds re-brand to the hub on sign-out — show the splash while the base
    // branding + domain config restore and images warm, then hide it.
    const sso = !!getEffectiveRootTenantId(env.rootTenantId);
    if (sso) await showNativeSplash();
    try {
      await useAuthStore.getState().signOut();
      if (sso) {
        await resetToHubContext(); // instant base restore + awaits the hub refresh
        await Promise.race([preloadBrandImages(), delay(SPLASH_MAX_WAIT_MS)]);
      }
    } finally {
      navigate('/login', { replace: true });
      if (sso) await hideNativeSplash();
    }
  };

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={settingsCopy.eyebrow}
        title={settingsCopy.title}
        subtitle={settingsCopy.subtitle}
      />

      <div className="px-6 pt-5 pb-safe-or-6">
        <Section title={settingsCopy.display}>
          <ListGroup>
            <ListItem
              title="Theme"
              subtitle="Pick your preference"
              leading={theme === 'dark' ? <Moon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} /> : theme === 'light' ? <Sun className="h-5 w-5 text-ink-muted" strokeWidth={1.5} /> : <Monitor className="h-5 w-5 text-ink-muted" strokeWidth={1.5} />}
              noChevron
              trailing={
                <SegmentedControl<ThemePref>
                  options={[{ value: 'system', label: 'Auto' }, { value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]}
                  value={theme}
                  onChange={setTheme}
                />
              }
            />
          </ListGroup>
        </Section>

        {platform.isNative() ? (
          <Section title={settingsCopy.security}>
            <ListGroup>
              <ListItem
                title="Biometric unlock"
                subtitle={biometricEnabled ? 'Enabled' : 'Use FaceID / fingerprint to keep you signed in'}
                leading={<Fingerprint className="h-5 w-5 text-ink-muted" strokeWidth={1.5} />}
                noChevron
                trailing={<Checkbox checked={biometricEnabled} onCheckedChange={setBiometricEnabled} />}
              />
            </ListGroup>
          </Section>
        ) : null}

        {platform.isNative() && env.push?.enabled ? (
          <Section title={settingsCopy.notifications}>
            <ListGroup>
              <ListItem
                title="Push notifications"
                subtitle={pushEnabled ? 'Enabled' : 'Off — turn on to receive alerts'}
                leading={<Bell className="h-5 w-5 text-ink-muted" strokeWidth={1.5} />}
                noChevron
                trailing={<Checkbox checked={pushEnabled} onCheckedChange={(on) => { setPushEnabled(on); toast.success(on ? 'Notifications on' : 'Notifications off'); }} />}
              />
            </ListGroup>
          </Section>
        ) : null}

        <Section title={settingsCopy.about}>
          <ListGroup>
            <ListItem title="Version" trailing={<span className="text-body text-ink-muted tabular">{env.version}</span>} leading={<Info className="h-5 w-5 text-ink-muted" strokeWidth={1.5} />} noChevron />
            <ListItem title="Build" trailing={<span className="text-body text-ink-muted">{env.buildMode}</span>} noChevron />
            {env.gitCommitSha ? (
              <ListItem title="Commit" trailing={<span className="text-body text-ink-muted tabular">{env.gitCommitSha}</span>} noChevron />
            ) : null}
          </ListGroup>
        </Section>

        <div className="mt-8">
          <ActionButton variant="ghost" className="text-danger" onClick={() => void onSignOut()}>
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Sign out
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
